import React, { useState } from "react"
import { CollapsibleSection } from "@/components/CollapsibleSection"
import { CitationsToggle } from "@/components/CitationsToggle"
import { ScrollArea } from "@/components/ui/scroll-area"

import { useActiveSection } from "@/lib/useActiveSection"
import { hasCutoff } from "@/lib/preview"
import { List } from "lucide-react"
import type { DocumentDetail, DocumentSection } from "@/lib/types"

interface DocumentViewerProps {
  document: DocumentDetail
  activityId?: number
  // The open book drawer shows the review as reference material only.
  hideCitationsToggle?: boolean
}

export function DocumentViewer({ document: doc, activityId, hideCitationsToggle = false }: DocumentViewerProps) {
  const [showCitations, setShowCitations] = useState(false)
  const hasActivities = doc.activities && doc.activities.length > 0

  // When activityId is set, filter to just that activity
  const selectedActivity = activityId && hasActivities
    ? doc.activities!.find((a) => a.id === activityId)
    : undefined

  const allSectionKeys = selectedActivity
    ? selectedActivity.sections.map((s) => s.section_key)
    : hasActivities
      ? doc.activities!.flatMap((a) => a.sections.map((s) => s.section_key))
      : doc.sections.map((s) => s.section_key)

  const activeSection = useActiveSection(allSectionKeys)
  const showCitationsToggle =
    !hideCitationsToggle &&
    (doc.document_type === "workflow_template" ||
    doc.document_type === "supplemental" ||
    doc.document_type === "clinical_review")

  const scrollToSection = (sectionKey: string) => (e: React.MouseEvent) => {
    e.preventDefault()
    const hash = `section-${sectionKey}`
    const el = document.getElementById(hash)
    if (!el) return
    const container = el.closest(".overflow-auto") as HTMLElement | null
    if (container) {
      const elTop = el.getBoundingClientRect().top
      const containerTop = container.getBoundingClientRect().top
      // 96px matches scroll-mt-24 on CollapsibleSection, leaving room
      // for the sticky title bar.
      container.scrollTo({
        top: container.scrollTop + elTop - containerTop - 96,
        behavior: "smooth",
      })
    } else {
      el.scrollIntoView({ behavior: "smooth" })
    }
    window.history.replaceState(null, "", `#${hash}`)
  }

  return (
    <div className={`flex gap-6 ${showCitations ? "show-citations" : ""}`}>
      {/* Main content */}
      <div className="min-w-0 flex-1">
        {/* Title + meta */}
        <div data-sticky-title className="sticky top-0 z-10 -mt-6 mb-6 flex items-start justify-between gap-4 bg-background pb-4 pt-6">
          <div>
            {selectedActivity && doc.clinical_review && (
              <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {doc.clinical_review.title}
              </p>
            )}
            <h1 className={`font-bold text-[#107D98] ${selectedActivity ? "text-2xl" : "text-3xl"}`}>
              {selectedActivity ? selectedActivity.name : doc.title}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              {doc.published_at && (
                <span className="text-xs text-muted-foreground">
                  Last Updated:{" "}
                  {new Date(doc.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" })}
                </span>
              )}
              {doc.compliance_tags?.map((tag) => (
                <span key={tag} className="text-xs text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          {showCitationsToggle && (
            <CitationsToggle
              enabled={showCitations}
              onChange={setShowCitations}
            />
          )}
        </div>

        {/* Sections */}
        {selectedActivity ? (
          <div className="space-y-2">
            {selectedActivity.sections.map((section, i) => (
              <CollapsibleSection
                key={section.id}
                section={section}
                previewCutoff={hasCutoff(doc.preview, selectedActivity.sections, i)}
              />
            ))}
          </div>
        ) : hasActivities ? (
          <div className="space-y-2">
            {doc.activities!.map((activity) => (
              <div key={activity.id} className="space-y-2">
                <h2 className="text-xl font-semibold border-l-4 border-l-primary bg-muted/50 px-4 py-2 rounded-r-md">
                  {activity.name}
                </h2>
                {activity.sections.map((section, i) => (
                  <CollapsibleSection
                    key={section.id}
                    section={section}
                    previewCutoff={hasCutoff(doc.preview, activity.sections, i)}
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {doc.sections.map((section, i) => (
              <CollapsibleSection
                key={section.id}
                section={section}
                previewCutoff={hasCutoff(doc.preview, doc.sections, i)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Table of Contents sidebar */}
      <aside className="hidden w-72 shrink-0 px-6 lg:block xl:w-80">
        <div className="sticky top-0 p-4 pt-6">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <List className="h-4 w-4" />
            Page Contents
          </div>
          <ScrollArea className="h-[calc(100vh-12rem)]">
            <nav className="space-y-0.5" onMouseDown={(e) => e.preventDefault()}>
              {selectedActivity
                ? selectedActivity.sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#section-${section.section_key}`}
                      className={`block border-l-2 py-1 pl-3 text-sm transition-colors ${
                        activeSection === section.section_key
                          ? "border-l-primary font-medium text-foreground"
                          : "border-l-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      onClick={scrollToSection(section.section_key)}
                    >
                      {section.heading}
                    </a>
                  ))
                : hasActivities
                  ? doc.activities!.map((activity) => (
                      <div key={activity.id} className="space-y-0.5">
                        <span className="block py-1 pl-3 text-sm font-medium text-foreground">
                          {activity.name}
                        </span>
                        <div className="ml-2 space-y-0.5">
                          {activity.sections.map((section) => (
                            <a
                              key={section.id}
                              href={`#section-${section.section_key}`}
                              className={`block border-l-2 py-0.5 pl-3 text-sm transition-colors ${
                                activeSection === section.section_key
                                  ? "border-l-primary font-medium text-foreground"
                                  : "border-l-transparent text-muted-foreground hover:text-foreground"
                              }`}
                              onClick={scrollToSection(section.section_key)}
                            >
                              {section.heading}
                            </a>
                          ))}
                        </div>
                      </div>
                    ))
                  : doc.sections.map((section) => (
                      <a
                        key={section.id}
                        href={`#section-${section.section_key}`}
                        className={`block border-l-2 py-1 pl-3 text-sm transition-colors ${
                          activeSection === section.section_key
                            ? "border-l-primary font-medium text-foreground"
                            : "border-l-transparent text-muted-foreground hover:text-foreground"
                        }`}
                        onClick={scrollToSection(section.section_key)}
                      >
                        {section.heading}
                      </a>
                    ))}
            </nav>
          </ScrollArea>
        </div>
      </aside>
    </div>
  )
}
